import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageTemplate from '../components/LegalPageTemplate';

const SIDEBAR_NAV = [
  {
    title: 'Privacy Policy',
    links: [
      { href: '#privacy-who', label: '1. Who we are' },
      { href: '#privacy-data', label: '2. Data we collect' },
      { href: '#privacy-purpose', label: '3. Purpose and legal basis' },
      { href: '#privacy-retention', label: '4. How long we keep your data' },
      { href: '#privacy-sharing', label: '5. Who we share data with' },
      { href: '#privacy-rights', label: '6. Your rights' },
      { href: '#privacy-cookies', label: '7. Cookies' },
      { href: '#privacy-security', label: '8. Security' },
      { href: '#privacy-contact', label: '9. Contact' },
    ],
  },
];

const PrivacyPolicyPage: React.FC = () => (
  <LegalPageTemplate
    title="Privacy Policy"
    description="How PH Therapy collects, uses, and protects your personal data."
    metaDescription="How PH Therapy collects, uses, and protects your personal data. Our privacy policy and your rights under GDPR."
    canonicalPath="/privacy"
    lastUpdated="February 2026"
    mainId="privacy-page"
    sidebarNav={SIDEBAR_NAV}
  >
    <section id="privacy-who">
      <h2>1. Who we are</h2>
      <p>PH Therapy (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a clinical exercise physiology practice. We are the data controller for personal data collected through this website.</p>
    </section>

    <section id="privacy-data">
      <h2>2. Data we collect</h2>
      <p>We may collect the following personal data:</p>
      <ul>
        <li><strong>Contact form:</strong> name, email address, and message content</li>
        <li><strong>Newsletter signup:</strong> email address and consent timestamp</li>
        <li><strong>Cookies:</strong> see section 7 below</li>
      </ul>
    </section>

    <section id="privacy-purpose">
      <h2>3. Purpose and legal basis</h2>
      <ul>
        <li><strong>Contact form:</strong> To respond to your enquiries. Legal basis: legitimate interest in providing services and communicating with potential clients.</li>
        <li><strong>Newsletter:</strong> To send you health tips, updates, and marketing communications. Legal basis: your consent (given when you subscribe and tick the consent checkbox).</li>
      </ul>
    </section>

    <section id="privacy-retention">
      <h2>4. How long we keep your data</h2>
      <p>We retain contact form submissions for as long as needed to resolve your enquiry and for a reasonable period afterwards (typically up to 2 years) for record-keeping. Newsletter data is kept until you unsubscribe. You may unsubscribe at any time.</p>
    </section>

    <section id="privacy-sharing">
      <h2>5. Who we share data with</h2>
      <p>Form submissions are processed by Netlify (our hosting provider). Netlify may store form data in accordance with their privacy policy. We do not sell your data to third parties. If we use additional services (e.g. email marketing tools), we will update this policy accordingly.</p>
    </section>

    <section id="privacy-rights">
      <h2>6. Your rights</h2>
      <p>Under UK GDPR, you have the right to:</p>
      <ul>
        <li><strong>Access</strong> your personal data</li>
        <li><strong>Rectify</strong> inaccurate data</li>
        <li><strong>Erase</strong> your data (&quot;right to be forgotten&quot;)</li>
        <li><strong>Restrict</strong> or <strong>object</strong> to processing</li>
        <li><strong>Data portability</strong> (where applicable)</li>
        <li><strong>Withdraw consent</strong> for consent-based processing (e.g. newsletter)</li>
        <li><strong>Complain</strong> to the Information Commissioner&apos;s Office (ICO): <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></li>
      </ul>
      <p>To exercise any of these rights, contact us using the details in section 9.</p>
    </section>

    <section id="privacy-cookies">
      <h2>7. Cookies</h2>
      <p>We use essential cookies to make the site function (e.g. remembering your cookie preference). If you accept non-essential cookies, we may use analytics (e.g. Google Analytics) to understand how visitors use our site. You can accept or decline non-essential cookies via the cookie banner shown on first visit. Your preference is stored in your browser.</p>
    </section>

    <section id="privacy-security">
      <h2>8. Security</h2>
      <p>We take reasonable steps to protect your data. Forms are submitted over HTTPS. We do not store form data on our servers beyond what our hosting provider requires for delivery.</p>
    </section>

    <section id="privacy-contact">
      <h2>9. Contact</h2>
      <p>For data protection enquiries or to exercise your rights, contact us via the <Link to="/contact">Contact page</Link> or the contact details on this website.</p>
    </section>
  </LegalPageTemplate>
);

export default PrivacyPolicyPage;
